import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { InstallationsService } from '@ubie/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'acl-installer-modal',
  standalone: false,
  templateUrl: './installer-modal.component.html',
  styleUrl: './installer-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallerModalComponent implements OnInit {
  private readonly modalController = inject(ModalController);
  private readonly formBuilder = inject(FormBuilder);
  private readonly installationsService = inject(InstallationsService);

  installerForm: FormGroup | undefined;
  readonly errorKey = signal<string | null>(null);
  readonly isSubmitting = signal(false);
  readonly _installationId = signal<number>(0);
  readonly _installerSlot = signal<1 | 2>(1);

  @Input() set installationId(value: number) {
    this._installationId.set(value);
  }

  @Input() set installerSlot(value: 1 | 2) {
    this._installerSlot.set(value ?? 1);
  }

  ngOnInit(): void {
    this.installerForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  async dismiss(event: string): Promise<void> {
    this.modalController.dismiss(event);
  }

  onSubmitInstallerAdd(): void {
    if (!this.installerForm?.valid || this.isSubmitting()) {
      return;
    }

    this.errorKey.set(null);
    this.isSubmitting.set(true);
    this.addInstallerToInstallation(this._installationId());
  }

  private addInstallerToInstallation(installationId: number): void {
    const payload = this.installerForm?.value as {
      email: string;
    };

    this.installationsService
      .addInstallerToInstallation(installationId, payload, {
        installerSlot: this._installerSlot(),
      })
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.dismiss('success');
        },
        error: (error) => {
          const key = this.getErrorKey(error);
          this.errorKey.set(key);
        },
      });
  }

  private getErrorKey(error: unknown): string {
    const getMessage = (value: unknown): string | null => {
      if (!value) {
        return null;
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value) as { Message?: string };
          return parsed?.Message ?? null;
        } catch {
          return value.trim() || null;
        }
      }

      if (typeof value === 'object') {
        const typed = value as { Message?: string; message?: string };
        return typed.Message ?? typed.message ?? null;
      }

      return null;
    };

    if (error instanceof HttpErrorResponse) {
      return getMessage(error.error) || 'general_error';
    }

    return getMessage(error) || 'general_error';
  }
}
